// McqResult.tsx

import React, { useEffect, useMemo, useState } from "react";
import { Box, Container, Typography, Card, CardContent, Button, Chip, Paper, Stack, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import TimerIcon from "@mui/icons-material/Timer";
import { PdfViewerDialog } from "@/components/PdfViewerDialog";
import type { QuestionAnswer, QuizSubmissionResponse } from "@/types/Questions.types";
import { getOptionText, joinLocalizedString } from "@/utils/commonUtils";
import { renderWithLineBreaks } from "@/utils/elementUtils";
import quizApi from "@/services/quizService";
import { useAppNavigate } from "@/hooks/useAppNavigate";

interface McqResultProps {
  quizName: string;
  paper: string;
  submissionId: string | null;
  submissionData?: QuizSubmissionResponse | null;
}

interface TSourceDialog {
  sentenceToHighlight: string;
  page: number;
}

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
};

const McqResult: React.FC<McqResultProps> = ({ quizName, paper, submissionId }) => {
  const navigate = useAppNavigate();
  const theme = useTheme();
  const [sourceDialogInfo, setSourceDialogInfo] = useState<TSourceDialog | null>(null);
  const [submissionData, setSubmissionData] = useState<QuizSubmissionResponse | null>(null);

  useEffect(() => {
    if (submissionId) {
      quizApi.getSubmissionById(submissionId).then((result) => {
        setSubmissionData(result);
      });
    }
  }, [submissionId]);

  // Normalize responses into a Map for O(1) lookup
  const responsesMap = useMemo(() => {
    if (!submissionData?.responses) return {};

    if (Array.isArray(submissionData.responses)) {
      return (submissionData.responses as QuestionAnswer[]).reduce(
        (acc, curr: QuestionAnswer) => {
          acc[curr.id] = curr;
          return acc;
        },
        {} as Record<string, QuestionAnswer>,
      );
    }

    return submissionData.responses as Record<string, QuestionAnswer>;
  }, [submissionData]);

  // Calculate score based on isCorrect flag
  const score = useMemo(() => {
    const total = submissionData?.responses.length;
    if (!total) return 0;
    const correctCount = submissionData?.responses.filter((q) => responsesMap[q.id]?.isCorrect).length;
    return Math.round(((correctCount ?? 0) / total) * 100);
  }, [submissionData, responsesMap]);

  const totalCorrect = submissionData?.responses.reduce((prev, current) => prev + (current.isCorrect ? 1 : 0), 0);

  return (
    <Container maxWidth="md" sx={{ pt: 8, pb: 8 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 5 },
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        {/* Header Summary */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 800, letterSpacing: "-0.02em", color: "text.primary" }}>
            Performance Review
          </Typography>

          <Box
            sx={{
              my: 2,
              px: 4,
              py: 2,
              borderRadius: 2,
              bgcolor: alpha(score >= 70 ? theme.palette.success.main : theme.palette.error.main, 0.08),
            }}
          >
            <Typography variant="h1" color={score >= 70 ? "success.main" : "error.main"} sx={{ fontWeight: 900 }}>
              {score}%
            </Typography>
          </Box>

          <Typography variant="body1" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
            You answered {totalCorrect} out of {submissionData?.responses.length} questions correctly
          </Typography>

          {submissionData && (
            <Stack direction="row" sx={{ mt: 2, gap: 2, justifyContent: "center" }}>
              <Chip
                icon={<TimerIcon />}
                label={`Time Used: ${formatTime(submissionData.totalTimeConsumed)}`}
                variant="outlined"
                size="small"
              />
              <Chip label={`Attempt #${submissionData.attemptNumber}`} variant="outlined" size="small" />
            </Stack>
          )}
        </Box>

        {/* Questions List */}
        <Stack spacing={4}>
          {submissionData?.responses.map((q, index) => {
            const res = responsesMap[q.id];
            const isCorrect = res?.isCorrect ?? false;
            const userAnswer = res?.selected.option || "";

            return (
              <Card
                key={q.id}
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: isCorrect ? alpha(theme.palette.success.main, 0.4) : alpha(theme.palette.error.main, 0.4),
                  bgcolor: "background.paper",
                }}
              >
                <CardContent sx={{ p: 4, "&:last-child": { pb: 4 } }}>
                  <Stack direction="row" sx={{ mb: 3, justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 800, textTransform: "uppercase" }}>
                      Question {index + 1}
                    </Typography>
                    <Chip
                      icon={isCorrect ? <CheckCircleIcon /> : <CancelIcon />}
                      label={isCorrect ? "Correct" : "Incorrect"}
                      color={isCorrect ? "success" : "error"}
                      size="small"
                    />
                  </Stack>

                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: "text.primary" }}>
                    {renderWithLineBreaks(joinLocalizedString(q.question))}
                  </Typography>

                  <Stack spacing={1.5} sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha(isCorrect ? theme.palette.success.main : theme.palette.error.main, 0.05),
                      }}
                    >
                      <Typography variant="body2" color={isCorrect ? "success.main" : "error.main"}>
                        <strong>Your Selection:</strong>{" "}
                        {userAnswer ? `(${userAnswer}) ${getOptionText(q, userAnswer)}` : "Skipped"}
                      </Typography>
                    </Box>
                    {!isCorrect && (
                      <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.success.main, 0.05) }}>
                        <Typography variant="body2" color="success.main">
                          <strong>Correct Answer:</strong> ({q.correctOption}) {getOptionText(q, q.correctOption)}
                        </Typography>
                      </Box>
                    )}
                  </Stack>

                  <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: "action.hover" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
                        Explanation
                      </Typography>
                      {res?.source && (
                        <Button
                          size="small"
                          startIcon={<MenuBookIcon />}
                          onClick={() => setSourceDialogInfo({ sentenceToHighlight: res.source, page: res.page })}
                          sx={{ textTransform: "none" }}
                        >
                          View Reference
                        </Button>
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {renderWithLineBreaks(joinLocalizedString(res?.explanation || q.explanation))}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Stack>

        <Box sx={{ mt: 6, textAlign: "center" }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => {
              if (submissionData?.quiz.name) {
                navigate(`mock/quiz/${submissionData?.quiz.name}/mcq`, {
                  state: {
                    quizId: submissionData.quiz.id,
                    exam: submissionData.quiz.name,
                    paper: submissionData.quiz.paper,
                    sections: submissionData.quiz.sections,
                    ids: submissionData.responses.map((r) => r.id),
                  },
                });
              }
            }}
            sx={{ px: 5, py: 1.8, fontWeight: 700 }}
          >
            Restart Mock Exam
          </Button>
        </Box>
      </Paper>

      {/* Reference Dialog */}
      <PdfViewerDialog
        quizName={quizName}
        paper={paper}
        sentenceToHighlights={sourceDialogInfo?.sentenceToHighlight.split("...") ?? []}
        open={Boolean(sourceDialogInfo)}
        onClose={() => setSourceDialogInfo(null)}
        page={sourceDialogInfo?.page ?? 1}
      />
    </Container>
  );
};

export default McqResult;
